import { participantsToOptions } from '@/api/userSearch/useCcBccUserOptions';
import {
  planRecipientOptions,
  planSenderOptions,
} from '../../initialValues/planRecipientOptions';
/**
 * Communication step for the Schedule Interview wizard.
 * Dual-pane layout: Candidate (left) | Interviewer (right).
 */
import {
  createRef,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import moment from 'moment-timezone';
import { formatTimezoneAbbreviationForDisplay } from '@/utils/formatTimezoneAbbreviation';
import { getCachedInterviewerOption } from '../../state/interviewerOptionCache';
import { buildEditorPreviewHtml } from '@kula-ai/components';
import type { EmailApiHandle } from '@kula-ai/components';
import { Flex } from '@chakra-ui/react';
import type { StepFormApi } from '@/hooks/useStepValidation';
import { useUploadUserInlineImage } from '@/api/userImageUploads';
import { useInterviewEmailTemplateOptions } from '../../../InterviewActivity/communication/api';
import { useInterviewActivityCommunicationVariables } from '../../../InterviewActivity/communication/api';
import {
  initialInterviewCommunicationFormState,
  interviewCommunicationReducer,
} from '../../../InterviewActivity/communication/state';
import {
  hydrateCommunicationStateFromPayload,
  toInterviewCommunicationPayload,
} from '../../../InterviewActivity/communication/helpers/payloadMap';
import {
  hasValidationErrors,
  validateInterviewCommunicationForm,
  type CommunicationValidationVariables,
  type InterviewCommunicationValidationErrors,
} from '../../../InterviewActivity/communication/validate';
import {
  getFirstScheduleCommunicationScrollTarget,
  scrollScheduleCommunicationSectionIntoView,
  type ScheduleCommunicationSectionKey,
} from './scheduleCommunicationScroll';
import { calendarInviteTemplateNameForApi } from '../../../InterviewActivity/communication/helpers/calendarInviteName';
import { getSenderFieldError } from '../../config/senderValidity';
import { SENDER_USE_CASE } from '../../config/senderErrorCopy';
import { compositionFromTemplateOption } from '../../../InterviewActivity/communication/helpers/templateApply';
import {
  cleanupStaleSenderValues,
  isBlankRichText,
} from '../../../InterviewActivity/communication/helpers/emailUtils';
import { seedSenderFrom } from './seedSenderFrom';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import {
  resolveDefaultFromValueForCurrentUser,
  useScheduleSenderOptions,
} from '../../hooks/useScheduleSenderOptions';
import type { ScheduleStepProps } from '../../config/types';
import { isAdhocMode } from '../../config/modeClassifiers';
import { CandidatePane } from './CandidatePane';
import { InterviewerPane } from './InterviewerPane';
import type { EmailSentBadgeProps } from '../../editMode/EmailSentBadge';

/** Calendar/email previews share a legacy BE quirk where unresolved variables
 *  arrive tagged with `style="color:red"`. Strip it so the components-side
 *  chip styling wins. */
const buildPreviewHtml = (
  html: string,
  resolved: Record<string, string>
): string =>
  buildEditorPreviewHtml(html, resolved, { stripInlineColorRed: true });

type SentEmailEntry = {
  type: string;
  recipient_id: number;
  recipient_name: string;
};

function buildSentBadge(
  entries: SentEmailEntry[]
): EmailSentBadgeProps | undefined {
  if (!entries.length) return undefined;

  // Count how many emails each unique recipient received.
  const perRecipient = new Map<number, { name: string; count: number }>();
  for (const e of entries) {
    const existing = perRecipient.get(e.recipient_id);
    if (existing) {
      existing.count += 1;
    } else {
      perRecipient.set(e.recipient_id, { name: e.recipient_name, count: 1 });
    }
  }

  // Group recipient names by their individual email count so the tooltip
  // reads "1 email to: Alice, Bob" (each got 1) or "2 emails to: Alice"
  // (Alice got 2) rather than collapsing unrelated totals.
  const byCount = new Map<number, string[]>();
  Array.from(perRecipient.values()).forEach(({ name, count }) => {
    const names = byCount.get(count) ?? [];
    names.push(name);
    byCount.set(count, names);
  });

  const recipients = Array.from(byCount.entries())
    .sort(([a], [b]) => a - b)
    .map(([count, names]) => ({ count, name: names.join(', ') }));

  return { totalSent: entries.length, recipients };
}

export const CommunicationStep = memo(
  forwardRef<
    StepFormApi,
    ScheduleStepProps & {
      interviewPlan?: Record<string, unknown>;
      candidateName?: string;
      jobTitle?: string;
    }
  >(
    (
      {
        payloadRef,
        mode,
        apiReference: _apiReference,
        prefillVersion = 0,
        interviewPlan,
        candidateName,
        jobTitle,
      },
      ref
    ) => {
      // ---- Reducer state ----
      const [state, dispatch] = useReducer(
        interviewCommunicationReducer,
        initialInterviewCommunicationFormState,
        (base) =>
          hydrateCommunicationStateFromPayload(
            Object.keys(payloadRef.current.communication).length > 0
              ? (payloadRef.current.communication as Partial<
                  Record<string, unknown>
                >)
              : undefined,
            base,
            { stripCalendarTemplates: prefillVersion > 0 }
          )
      );

      const { candidateSentBadge, interviewerSentBadge } = useMemo(() => {
        const all =
          (interviewPlan?.sent_emails as SentEmailEntry[] | undefined) ?? [];
        const candidateEmails = all.filter((e) => e.type === 'candidate');
        const interviewerEmails = all.filter((e) => e.type === 'interviewer');
        return {
          candidateSentBadge: buildSentBadge(candidateEmails),
          interviewerSentBadge: buildSentBadge(interviewerEmails),
        };
      }, [interviewPlan]);

      // The wizard's self-schedule email is owned end-to-end by the
      // dedicated SlotSelectionEmailStep (renders it, validates it, and
      // emits the payload via `mapSlotEmailToApi` — `mapCommunication`
      // never emits `self_schedule`). The Communication step must NOT
      // validate its own duplicate, empty, non-rendered `selfSchedule`
      // block: in self-schedule edit it reported "Subject/Body required"
      // and silently blocked Update with no visible error. Always false
      // here; the effect below then clears `selfScheduleEnabled`.
      //
      // Panel gating only. The sender errors are folded in further down, once
      // `useScheduleSenderOptions` has run — they can't be computed here.
      const commValidationFlags = useMemo(
        () => ({
          validateSelfSchedule: false,
          // The candidate-availability email panel only exists on the
          // InterviewActivity plan editor. This dual-pane step never renders
          // it, so its always-on (empty) availability block must not block save.
          validateAvailability: false,
        }),
        []
      );

      useEffect(() => {
        if (commValidationFlags.validateSelfSchedule) {
          return;
        }
        dispatch({ type: 'SET_SELF_SCHEDULE_ENABLED', payload: false });
      }, [dispatch, commValidationFlags.validateSelfSchedule]);

      const [fieldErrors, setFieldErrors] =
        useState<InterviewCommunicationValidationErrors>({});

      /**
       * Bumped on every `validate()` call so each Email re-fires
       * `focusFirstError` on repeat submits, even when the error map content
       * is unchanged. The empty→populated transition trigger handles the
       * first submit; this counter handles the rest.
       */
      const [submitAttempt, setSubmitAttempt] = useState(0);

      /**
       * Stable per-section refs to each Email's imperative handle. `validate()`
       * calls `focusFirstError()` on the offending section's ref to land on the
       * bad field/variable — bypassing the kula module-level auto-scroll dedupe
       * guard so the right editor is targeted with several editors mounted.
       * (The shared `TemplatedEmail` disables the racy auto-scroll.)
       */
      const emailApiRefs = useMemo<
        Record<
          ScheduleCommunicationSectionKey,
          ReturnType<typeof createRef<EmailApiHandle>>
        >
      >(
        () => ({
          candidateCalendar: createRef<EmailApiHandle>(),
          candidateEmail: createRef<EmailApiHandle>(),
          interviewerCalendar: createRef<EmailApiHandle>(),
          interviewerEmail: createRef<EmailApiHandle>(),
          selfSchedule: createRef<EmailApiHandle>(),
          // No availability panel in this dual-pane step; ref present to satisfy
          // the shared section-key Record (same as selfSchedule above).
          availability: createRef<EmailApiHandle>(),
        }),
        []
      );
      /** Debounces the scroll against the double `validate()` call. */
      const lastErrorScrollAtRef = useRef(0);

      // ---- Data fetching ----
      // Match InterviewActivity's per-section fetches so the calendar
      // dropdown only shows calendar-category templates and the email
      // dropdown only shows email-category templates (legacy wizard did
      // the same — a single merged scope left the wrong list in each
      // dropdown and picked the wrong default template).
      const { data: candidateCalendarTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('candidate-calendar');
      const { data: candidateEmailTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('candidate-email');
      const { data: interviewerCalendarTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('interviewer-calendar');
      const { data: interviewerEmailTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('interviewer-email');
      const { data: selfScheduleTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('self-schedule');
      // Saved recipients label their own chips; the first search page may not
      // contain them (ENG-3766).
      const savedCcBccOptions = useMemo(() => {
        const comm = (payloadRef.current?.communication ?? {}) as Record<
          string,
          any
        >;
        // The wizard's initial values keep ids only, so the raw plan is the
        // only place the participant objects survive.
        return [
          ...planRecipientOptions(interviewPlan),
          ...Object.values(comm).flatMap((cfg: any) => [
            ...participantsToOptions(cfg?.cc_participants),
            ...participantsToOptions(cfg?.bcc_participants),
          ]),
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [prefillVersion, interviewPlan]);

      // Same for the saved sender — it may not be delegatable any more.
      const savedFromOptions = useMemo(() => {
        const comm = (payloadRef.current?.communication ?? {}) as Record<
          string,
          any
        >;
        return [
          ...planSenderOptions(interviewPlan),
          ...Object.values(comm).flatMap((cfg: any) =>
            participantsToOptions([cfg?.sender])
          ),
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [prefillVersion, interviewPlan]);

      const {
        fromOptions,
        validFromValues,
        ccBccOptions,
        ccBccSelectProps,
        fromSelectProps,
        isFromOptionsFetched,
        isFromOptionsLoading,
      } = useScheduleSenderOptions({
        includeSharedMailboxes: true,
        knownCcBccOptions: savedCcBccOptions,
        knownFromOptions: savedFromOptions,
      });

      // Name + status of the senders the interview was saved with. Once one
      // stops being delegatable the picker can no longer describe them, so the
      // message has to be built from here (ENG-3916).
      const savedSenders = useMemo(() => {
        const comm = (payloadRef.current?.communication ?? {}) as Record<
          string,
          any
        >;
        const pick = (cfg: any) => cfg?.senderParticipant ?? cfg?.sender;
        return {
          candidateEmail: pick(comm?.candidate_email_comm_config),
          interviewerEmail: pick(comm?.interviewer_email_comm_config),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [prefillVersion, interviewPlan]);

      // Panel gating plus the per-block sender check. Declared here rather than
      // beside the flags because it reads `validFromValues`, which only exists
      // once the hook above has run.
      const validateCommOptions = useMemo(
        () => ({
          ...commValidationFlags,
          senderErrors: {
            candidateEmail: getSenderFieldError({
              from: state.candidateEmail.from,
              validFromValues,
              savedSender: savedSenders.candidateEmail,
              isFromOptionsFetched,
              useCase: SENDER_USE_CASE.candidateConfirmation,
            }),
            interviewerEmail: getSenderFieldError({
              from: state.interviewerEmail.from,
              validFromValues,
              savedSender: savedSenders.interviewerEmail,
              isFromOptionsFetched,
              useCase: SENDER_USE_CASE.interviewerConfirmation,
            }),
          },
        }),
        [
          commValidationFlags,
          state.candidateEmail.from,
          state.interviewerEmail.from,
          validFromValues,
          savedSenders,
          isFromOptionsFetched,
        ]
      );

      // The shared initial state seeds `from: 'organizer'` — that
      // sentinel is correct on InterviewActivity (stage-action plan),
      // but for a specific scheduled interview the From should default
      // to the user creating it. Swap each email's `from` once on mount
      // iff (a) it still holds the organizer sentinel AND (b) the
      // current user is actually delegatable. The second check closes
      // the bug where an admin-only user had their id written into
      // `from`, the Select showed empty (id not in fromOptions), and
      // the validator reported the form valid anyway.
      const currUser = useSelector(selectCurrUser) as
        | {
            id?: number;
            first_name?: string;
            last_name?: string;
            email?: string;
          }
        | null
        | undefined;
      // ---- Preview state (per editor block) ----
      const [candidateCalPreviewHtml, setCandidateCalPreviewHtml] = useState<
        string | undefined
      >(undefined);
      const [candidateEmailPreviewHtml, setCandidateEmailPreviewHtml] =
        useState<string | undefined>(undefined);
      const [interviewerCalPreviewHtml, setInterviewerCalPreviewHtml] =
        useState<string | undefined>(undefined);
      const [interviewerEmailPreviewHtml, setInterviewerEmailPreviewHtml] =
        useState<string | undefined>(undefined);

      const buildVars = useCallback(
        (isInterviewer = false): Record<string, string> => {
          const details = payloadRef.current.details as Record<string, unknown>;

          // Interview name
          const interviewName =
            typeof details.interviewName === 'string'
              ? details.interviewName
              : undefined;

          // Interview time — bookedSlot (manual) or first self-schedule slot
          const bookedSlot = details.bookedSlot as
            | { start?: string }
            | undefined;
          const selfScheduleSlots = details.selfScheduleSlots as
            | { start?: string }[]
            | undefined;
          const startIso =
            bookedSlot?.start ?? selfScheduleSlots?.[0]?.start ?? undefined;
          const timezone =
            typeof details.timezone === 'string' ? details.timezone : undefined;
          let interviewTime: string | undefined;
          if (startIso && timezone) {
            const m = moment.tz(startIso, timezone);
            const abbr = formatTimezoneAbbreviationForDisplay(m, timezone);
            interviewTime = `${m.format('MMM DD, YYYY, h:mm A')} ${abbr}`;
          }

          // Duration
          const durationRaw = details.duration;
          const interviewDuration =
            durationRaw !== undefined && durationRaw !== null
              ? `${durationRaw} minutes`
              : undefined;

          // Onsite location — mirrors legacy generateLocationWithMapsLink
          let interviewOnsiteLocation: string | undefined;
          if (details.location === 'onsite') {
            const office = (
              interviewPlan as Record<string, unknown> | undefined
            )?.ats_office as
              | { name?: string; location?: string; place_id?: string }
              | undefined;
            if (office?.place_id && office.location) {
              const label = isInterviewer
                ? `${office.name ?? ''}, ${office.location}`.replace(
                    /^,\s*/,
                    ''
                  )
                : office.location;
              const mapsUrl = `https://www.google.com/maps/place/?q=place_id:${office.place_id}`;
              interviewOnsiteLocation = `${label} (<a href="${mapsUrl}" target="_blank" rel="noopener noreferrer" style="color:#6C63FF;text-decoration:underline;">View on maps</a>)`;
            }
          }

          // Interviewers — look up names from module-level cache
          const rawIds = details.participant_ids;
          const participantIds: string[] = (
            Array.isArray(rawIds) ? rawIds : rawIds ? [rawIds] : []
          ).map(String);
          const interviewerNames = participantIds
            .map((id) => getCachedInterviewerOption(id)?.label)
            .filter((n): n is string => Boolean(n));
          const primaryInterviewer = interviewerNames[0];
          const allInterviewers = interviewerNames.join(', ');

          // Candidate name fields
          const parts = candidateName
            ? candidateName.trim().split(/\s+/)
            : undefined;
          const firstName = parts?.[0];
          const lastName =
            parts && parts.length > 1 ? parts.slice(1).join(' ') : undefined;

          // Sender fields from currUser
          const senderFirst = currUser?.first_name;
          const senderLast = currUser?.last_name;
          const senderFull =
            senderFirst || senderLast
              ? `${senderFirst ?? ''} ${senderLast ?? ''}`.trim()
              : undefined;

          const vars: Record<string, string> = {};
          const set = (key: string, val: string | undefined) => {
            if (val !== undefined && val !== '') vars[`{{${key}}}`] = val;
          };

          set('interview.name', interviewName);
          set('interview.time', interviewTime);
          set('interview.duration', interviewDuration);
          set('interview.onsite_location', interviewOnsiteLocation);
          set('interview.interviewer_full_name', primaryInterviewer);
          set('interview.all_interviewers_full_names', allInterviewers);
          // Legacy also has first/last name variants
          set(
            'interview.interviewer_first_name',
            primaryInterviewer?.split(' ')[0]
          );
          set(
            'interview.interviewer_last_name',
            primaryInterviewer?.split(' ').slice(1).join(' ')
          );
          set('candidate.first_name', firstName);
          set('candidate.last_name', lastName);
          set('candidate.full_name', candidateName?.trim());
          set('job.title', jobTitle);
          set('sender.first_name', senderFirst);
          set('sender.last_name', senderLast);
          set('sender.full_name', senderFull);
          set('sender.email', currUser?.email);

          return vars;
        },
        [candidateName, currUser, interviewPlan, jobTitle, payloadRef]
      );
      // Combined seed + cleanup. Both transforms read closure state and
      // dispatch a full composition payload (the reducer replaces the
      // whole `*.email` object). When kept as separate effects they
      // batched dispatches with stale payloads and the cleanup's
      // closure-captured `from` snapped a freshly-seeded user back to
      // 'organizer'. Compose them: seed first, then cleanup, then
      // dispatch once per email.
      const didInitFromRef = useRef(false);
      useEffect(() => {
        if (didInitFromRef.current) return;
        if (!isFromOptionsFetched) return;
        didInitFromRef.current = true;

        // Empty when the scheduler's own mailbox isn't connected — see
        // `seedSenderFrom`, which then clears the sentinel rather than
        // seeding an arbitrary teammate.
        const seedValue = resolveDefaultFromValueForCurrentUser(
          fromOptions,
          currUser?.id
        );

        const fromOptionValues = new Set(
          fromOptions.map((o) => String(o.value))
        );
        // No `ccBccOptionValues`: CC/BCC now searches server-side, so the
        // loaded page is not the full list and pruning against it would delete
        // valid saved recipients (ENG-3766).
        const cleanupOpts = { fromOptionValues };

        const transform = (
          composition: typeof state.candidateEmail
        ): typeof composition | null => {
          let next = composition;
          let changed = false;
          // Step 1 (seed): replace the organizer sentinel with the
          // resolved seed value — including the empty one, which leaves
          // From blank for the recruiter to fill in.
          const seeded = seedSenderFrom(next, seedValue);
          if (seeded) {
            next = seeded;
            changed = true;
          }
          // Step 2 (cleanup): drop any from / cc / bcc values whose
          // referenced user or shared mailbox is no longer in the
          // picker — handles plan templates that named teammates who
          // have since been removed from the org.
          const cleaned = cleanupStaleSenderValues(next, cleanupOpts);
          if (cleaned) {
            next = cleaned;
            changed = true;
          }
          return changed ? next : null;
        };

        const candidateUpdate = transform(state.candidateEmail);
        if (candidateUpdate) {
          dispatch({ type: 'SET_CANDIDATE_EMAIL', payload: candidateUpdate });
        }
        const interviewerUpdate = transform(state.interviewerEmail);
        if (interviewerUpdate) {
          dispatch({
            type: 'SET_INTERVIEWER_EMAIL',
            payload: interviewerUpdate,
          });
        }
        const selfScheduleUpdate = transform(state.selfSchedule);
        if (selfScheduleUpdate) {
          dispatch({ type: 'SET_SELF_SCHEDULE', payload: selfScheduleUpdate });
        }
        // One-shot, gated by ref — reads state once at fire time.
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [currUser?.id, isFromOptionsFetched, fromOptions, ccBccOptions]);

      const {
        candidateCalendar: candidateCalendarVariables,
        interviewerCalendar: interviewerCalendarVariables,
        candidateEmail: candidateEmailVariables,
        interviewerEmail: interviewerEmailVariables,
      } = useInterviewActivityCommunicationVariables();

      const { mutateAsync: uploadUserInlineImageAsync } =
        useUploadUserInlineImage();
      const uploadInlineImage = useCallback(
        (file: File) => uploadUserInlineImageAsync(file),
        [uploadUserInlineImageAsync]
      );

      // ---- Validation variables ----
      const validationVariables: CommunicationValidationVariables = useMemo(
        () => ({
          candidateCalendar: candidateCalendarVariables,
          interviewerCalendar: interviewerCalendarVariables,
          candidateEmail: candidateEmailVariables,
          interviewerEmail: interviewerEmailVariables,
        }),
        [
          candidateCalendarVariables,
          candidateEmailVariables,
          interviewerCalendarVariables,
          interviewerEmailVariables,
        ]
      );

      // ---- Live validation after first submit failure ----
      useEffect(() => {
        setFieldErrors((prev) => {
          if (!hasValidationErrors(prev)) return prev;
          return validateInterviewCommunicationForm(
            state,
            validationVariables,
            validateCommOptions
          );
        });
      }, [state, validationVariables, validateCommOptions]);

      // ---- One-shot template defaults ----
      // Calendar invites stay adhoc-create-only (gated below). Email
      // sections (candidate / interviewer / self-schedule) instead fall
      // back to the first template whenever the section hydrates blank —
      // adhoc, or a legacy plan that predates these now-mandatory
      // sections. A plan/edit hydration with real subject/body is
      // detected by `isBlankRichText` and left untouched, so flows
      // created in the new feature never get clobbered.
      const isAdhoc = isAdhocMode(mode);
      const didDefaultCandidateCal = useRef(false);
      const didDefaultCandidateEmail = useRef(false);
      const didDefaultInterviewerCal = useRef(false);
      const didDefaultInterviewerEmail = useRef(false);
      const didDefaultSelfSchedule = useRef(false);

      useEffect(() => {
        if (!isAdhoc) return;
        if (prefillVersion > 0 || !candidateCalendarTemplateOptions.length) {
          return;
        }
        if (state.candidateCalendar.template) {
          didDefaultCandidateCal.current = true;
          return;
        }
        if (didDefaultCandidateCal.current) return;
        didDefaultCandidateCal.current = true;
        dispatch({
          type: 'SET_CANDIDATE_CALENDAR',
          payload: compositionFromTemplateOption(
            state.candidateCalendar,
            candidateCalendarTemplateOptions[0]
          ),
        });
      }, [
        isAdhoc,
        prefillVersion,
        candidateCalendarTemplateOptions,
        state.candidateCalendar,
      ]);

      useEffect(() => {
        if (!candidateEmailTemplateOptions.length) return;
        // Skip only when the section already carries real content — an
        // adhoc-picked template, or a plan/edit hydration with non-blank
        // subject/body (created in the new feature). A legacy activity
        // (its plan predates this mandatory section) hydrates blank and
        // falls through to the first-template fallback, same as adhoc.
        if (
          state.candidateEmail.template ||
          !isBlankRichText(state.candidateEmail.subject) ||
          !isBlankRichText(state.candidateEmail.content)
        ) {
          didDefaultCandidateEmail.current = true;
          return;
        }
        if (didDefaultCandidateEmail.current) return;
        didDefaultCandidateEmail.current = true;
        // PATCH (not SET) so we don't clobber `from` set by the seed
        // effect when both fire on the same render — the seed dispatch
        // and this dispatch both read the same closure-captured state,
        // so a SET here would race-overwrite the seeded from with the
        // organizer sentinel.
        const opt = candidateEmailTemplateOptions[0];
        dispatch({
          type: 'PATCH_CANDIDATE_EMAIL',
          payload: {
            template: opt.value,
            content: String(opt.data?.body ?? ''),
            subject: String(opt.data?.subject ?? ''),
          },
        });
      }, [candidateEmailTemplateOptions, state.candidateEmail]);

      useEffect(() => {
        if (!isAdhoc) return;
        if (prefillVersion > 0 || !interviewerCalendarTemplateOptions.length) {
          return;
        }
        if (state.interviewerCalendar.template) {
          didDefaultInterviewerCal.current = true;
          return;
        }
        if (didDefaultInterviewerCal.current) return;
        didDefaultInterviewerCal.current = true;
        dispatch({
          type: 'SET_INTERVIEWER_CALENDAR',
          payload: compositionFromTemplateOption(
            state.interviewerCalendar,
            interviewerCalendarTemplateOptions[0]
          ),
        });
      }, [
        isAdhoc,
        prefillVersion,
        interviewerCalendarTemplateOptions,
        state.interviewerCalendar,
      ]);

      useEffect(() => {
        if (!interviewerEmailTemplateOptions.length) return;
        // See candidateEmail note: skip only when real content exists.
        if (
          state.interviewerEmail.template ||
          !isBlankRichText(state.interviewerEmail.subject) ||
          !isBlankRichText(state.interviewerEmail.content)
        ) {
          didDefaultInterviewerEmail.current = true;
          return;
        }
        if (didDefaultInterviewerEmail.current) return;
        didDefaultInterviewerEmail.current = true;
        // PATCH (not SET) — see note on candidateEmail above.
        const opt = interviewerEmailTemplateOptions[0];
        dispatch({
          type: 'PATCH_INTERVIEWER_EMAIL',
          payload: {
            template: opt.value,
            content: String(opt.data?.body ?? ''),
            subject: String(opt.data?.subject ?? ''),
          },
        });
      }, [interviewerEmailTemplateOptions, state.interviewerEmail]);

      useEffect(() => {
        if (!selfScheduleTemplateOptions.length) return;
        // Mode-applicability guard (kept): self-schedule email only
        // applies to self-schedule-family modes.
        if (!commValidationFlags.validateSelfSchedule) return;
        // See candidateEmail note: skip only when real content exists.
        if (
          state.selfSchedule.template ||
          !isBlankRichText(state.selfSchedule.subject) ||
          !isBlankRichText(state.selfSchedule.content)
        ) {
          didDefaultSelfSchedule.current = true;
          return;
        }
        if (didDefaultSelfSchedule.current) return;
        didDefaultSelfSchedule.current = true;
        // PATCH (not SET) — see note on candidateEmail above.
        const opt = selfScheduleTemplateOptions[0];
        dispatch({
          type: 'PATCH_SELF_SCHEDULE',
          payload: {
            template: opt.value,
            content: String(opt.data?.body ?? ''),
            subject: String(opt.data?.subject ?? ''),
          },
        });
      }, [
        selfScheduleTemplateOptions,
        state.selfSchedule,
        commValidationFlags.validateSelfSchedule,
      ]);

      // ---- Imperative handle ----
      useImperativeHandle(
        ref,
        () => ({
          validate: async () => {
            const errors = validateInterviewCommunicationForm(
              state,
              validationVariables,
              validateCommOptions
            );
            setFieldErrors(errors);
            /** Force every Email to surface its errors (red borders) on submit. */
            setSubmitAttempt((n) => n + 1);
            const ok = !hasValidationErrors(errors);
            if (!ok) {
              const target = getFirstScheduleCommunicationScrollTarget(errors);
              const now =
                typeof performance !== 'undefined'
                  ? performance.now()
                  : Date.now();
              const debounced = now - lastErrorScrollAtRef.current <= 250;
              if (target && !debounced) {
                lastErrorScrollAtRef.current = now;
                // Defer so the step's DOM is mounted/laid-out on cross-step
                // navigation (Save on step 1 → invalid step 2 mounts). Reveal
                // the section, then land on the exact bad field/variable via the
                // target Email's handle (`focusFirstError()` bypasses the kula
                // auto-scroll dedupe guard). Retry once for mount/layout timing.
                const runScroll = () => {
                  scrollScheduleCommunicationSectionIntoView(target.sectionId);
                  emailApiRefs[target.key].current?.focusFirstError();
                };
                window.setTimeout(runScroll, 160);
                window.setTimeout(runScroll, 360);
              }
            }
            return ok;
          },
          syncToPayload: () => {
            const comm = toInterviewCommunicationPayload(state, {
              currentUserId: currUser?.id,
            });
            comm._ui_calendar_candidate_name = calendarInviteTemplateNameForApi(
              state.candidateCalendar,
              candidateCalendarTemplateOptions
            );
            comm._ui_calendar_interviewer_name =
              calendarInviteTemplateNameForApi(
                state.interviewerCalendar,
                interviewerCalendarTemplateOptions
              );
            payloadRef.current.communication = comm as unknown as Record<
              string,
              unknown
            >;
          },
        }),
        [
          candidateCalendarTemplateOptions,
          currUser?.id,
          interviewerCalendarTemplateOptions,
          payloadRef,
          state,
          validationVariables,
          validateCommOptions,
          emailApiRefs,
        ]
      );

      // ---- Render ----
      return (
        <Flex
          key={String(prefillVersion)}
          w="100%"
          h="100%"
          minH={0}
          flex="1"
          gap="6"
          overflow="auto"
          pb="8"
        >
          <CandidatePane
            state={state}
            dispatch={dispatch}
            fieldErrors={fieldErrors}
            submitAttempt={submitAttempt}
            calendarApiRef={emailApiRefs.candidateCalendar}
            emailApiRef={emailApiRefs.candidateEmail}
            calendarTemplateOptions={candidateCalendarTemplateOptions}
            emailTemplateOptions={candidateEmailTemplateOptions}
            fromOptions={fromOptions}
            isFromOptionsLoading={isFromOptionsLoading}
            ccBccOptions={ccBccOptions}
            ccBccSelectProps={ccBccSelectProps}
            fromSelectProps={fromSelectProps}
            calendarVariables={candidateCalendarVariables}
            emailVariables={candidateEmailVariables}
            uploadInlineImage={uploadInlineImage}
            sentEmailBadge={candidateSentBadge}
            calendarPreviewProps={{
              previewHtml: candidateCalPreviewHtml,
              onPreviewClick: () =>
                setCandidateCalPreviewHtml(
                  buildPreviewHtml(state.candidateCalendar.content, buildVars())
                ),
              onPreviewDismiss: () => setCandidateCalPreviewHtml(undefined),
            }}
            emailPreviewProps={{
              previewHtml: candidateEmailPreviewHtml,
              onPreviewClick: () =>
                setCandidateEmailPreviewHtml(
                  buildPreviewHtml(state.candidateEmail.content, buildVars())
                ),
              onPreviewDismiss: () => setCandidateEmailPreviewHtml(undefined),
            }}
          />
          <InterviewerPane
            state={state}
            dispatch={dispatch}
            fieldErrors={fieldErrors}
            submitAttempt={submitAttempt}
            calendarApiRef={emailApiRefs.interviewerCalendar}
            emailApiRef={emailApiRefs.interviewerEmail}
            calendarTemplateOptions={interviewerCalendarTemplateOptions}
            emailTemplateOptions={interviewerEmailTemplateOptions}
            fromOptions={fromOptions}
            isFromOptionsLoading={isFromOptionsLoading}
            ccBccOptions={ccBccOptions}
            ccBccSelectProps={ccBccSelectProps}
            fromSelectProps={fromSelectProps}
            calendarVariables={interviewerCalendarVariables}
            emailVariables={interviewerEmailVariables}
            uploadInlineImage={uploadInlineImage}
            sentEmailBadge={interviewerSentBadge}
            calendarPreviewProps={{
              previewHtml: interviewerCalPreviewHtml,
              onPreviewClick: () =>
                setInterviewerCalPreviewHtml(
                  buildPreviewHtml(
                    state.interviewerCalendar.content,
                    buildVars(true)
                  )
                ),
              onPreviewDismiss: () => setInterviewerCalPreviewHtml(undefined),
            }}
            emailPreviewProps={{
              previewHtml: interviewerEmailPreviewHtml,
              onPreviewClick: () =>
                setInterviewerEmailPreviewHtml(
                  buildPreviewHtml(
                    state.interviewerEmail.content,
                    buildVars(true)
                  )
                ),
              onPreviewDismiss: () => setInterviewerEmailPreviewHtml(undefined),
            }}
          />
        </Flex>
      );
    }
  )
);

CommunicationStep.displayName = 'CommunicationStep';
